import axios from 'axios';
import { mainApiUrl } from '../config';
import type { ProposalServiceFile } from './proposalServiceDetails';

const mainApi = () => {
  const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
  return axios.create({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

const fileName = (file: ProposalServiceFile) => file.name || 'ficheiro';

const isImageFile = (file: ProposalServiceFile): boolean => {
  const type = (file.type || '').toLowerCase();
  const name = fileName(file).toLowerCase();
  return (
    type.includes('image') ||
    ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some((ext) => name.endsWith(ext))
  );
};

const isPdfFile = (file: ProposalServiceFile): boolean => {
  const type = (file.type || '').toLowerCase();
  return type.includes('pdf') || fileName(file).toLowerCase().endsWith('.pdf');
};

async function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

async function fetchLeadFileBlob(
  file: ProposalServiceFile,
  leadId: number | null
): Promise<Blob | null> {
  const api = mainApi();
  const fileId = file.lead_file_id || file.file_id;
  const lid = Number(file.lead_id || leadId);

  if (lid && fileId) {
    try {
      const res = await api.get(`/api/leads/${lid}/files/${fileId}/download`, {
        responseType: 'blob',
      });
      return res.data as Blob;
    } catch {
      /* try fallback */
    }
    try {
      const res = await api.get(`/api/leads/files/${fileId}/download`, {
        responseType: 'blob',
      });
      return res.data as Blob;
    } catch {
      return null;
    }
  }

  if (file.data) {
    try {
      const raw = file.data.startsWith('data:') ? file.data.split(',')[1] : file.data;
      const byteCharacters = atob(raw);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([new Uint8Array(byteNumbers)], {
        type: file.type || 'application/octet-stream',
      });
    } catch {
      return null;
    }
  }

  const publicId = file.public_id;
  if (publicId) {
    try {
      const res = await api.get(`/api/chat/download/${encodeURIComponent(publicId)}`);
      if (res.data?.success && res.data.download_url) {
        const fileRes = await fetch(res.data.download_url);
        if (fileRes.ok) return await fileRes.blob();
      }
    } catch {
      return null;
    }
  }

  if (file.url) {
    try {
      const fullUrl = file.url.startsWith('http')
        ? file.url
        : mainApiUrl(file.url.startsWith('/') ? file.url : `/${file.url}`);
      const res = await api.get(fullUrl, { responseType: 'blob' });
      return res.data as Blob;
    } catch {
      return null;
    }
  }

  return null;
}

export async function downloadProposalFile(
  file: ProposalServiceFile,
  leadId: number | null
): Promise<void> {
  const blob = await fetchLeadFileBlob(file, leadId);
  if (blob) {
    await downloadBlob(blob, fileName(file));
    return;
  }
  window.alert(`Não foi possível descarregar "${fileName(file)}".`);
}

export type FilePreviewState = {
  url: string;
  name: string;
  type: string;
  isImage: boolean;
  isPdf: boolean;
};

export async function loadProposalFilePreview(
  file: ProposalServiceFile,
  leadId: number | null
): Promise<FilePreviewState | null> {
  if (file.data) {
    const url = file.data.startsWith('data:') ? file.data : `data:${file.type || 'application/octet-stream'};base64,${file.data}`;
    return {
      url,
      name: fileName(file),
      type: file.type || '',
      isImage: isImageFile(file),
      isPdf: isPdfFile(file),
    };
  }

  const api = mainApi();
  const fileId = file.lead_file_id || file.file_id;
  const lid = Number(file.lead_id || leadId);

  if (lid && fileId) {
    if (isPdfFile(file)) {
      try {
        const res = await api.get(`/api/leads/${lid}/files/${fileId}/preview-base64`);
        if (res.data?.success && res.data.pdf_data) {
          const pdfData = String(res.data.pdf_data).startsWith('data:')
            ? res.data.pdf_data
            : `data:application/pdf;base64,${res.data.pdf_data}`;
          return {
            url: pdfData,
            name: fileName(file),
            type: 'application/pdf',
            isImage: false,
            isPdf: true,
          };
        }
      } catch {
        /* fall through to blob */
      }
    }
    const blob = await fetchLeadFileBlob(file, leadId);
    if (blob) {
      return {
        url: URL.createObjectURL(blob),
        name: fileName(file),
        type: file.type || blob.type,
        isImage: isImageFile(file),
        isPdf: isPdfFile(file),
      };
    }
  }

  const publicId = file.public_id;
  if (publicId) {
    try {
      const res = await api.get(`/api/chat/download/${encodeURIComponent(publicId)}`);
      if (res.data?.success && res.data.download_url) {
        return {
          url: res.data.download_url,
          name: fileName(file),
          type: file.type || '',
          isImage: isImageFile(file),
          isPdf: isPdfFile(file),
        };
      }
    } catch {
      return null;
    }
  }

  const blob = await fetchLeadFileBlob(file, leadId);
  if (blob) {
    const url = URL.createObjectURL(blob);
    return {
      url,
      name: fileName(file),
      type: file.type || blob.type,
      isImage: isImageFile(file),
      isPdf: isPdfFile(file),
    };
  }

  return null;
}
